import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-video-player-modal',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="video-modal-overlay" *ngIf="isOpen" (click)="onOverlayClick($event)">
      <div class="video-modal">
        <div class="video-modal__header">
          <h5 class="video-modal__title">{{ title }}</h5>
          <button type="button" class="video-modal__close" (click)="close()">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="video-modal__body">
          <div class="video-modal__loader" *ngIf="loading">
            <i class="fas fa-circle-notch fa-spin"></i>
            <span>Loading recording…</span>
          </div>
          <video #videoEl [src]="src" controls autoplay class="video-modal__player" *ngIf="!loading"></video>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .video-modal-overlay {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.7);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
      padding: 5vh 5vw;
    }

    .video-modal {
      background: #fff;
      border-radius: 8px;
      width: 90vw;
      max-height: 90vh;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    .video-modal__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px 20px;
      border-bottom: 1px solid #eee;
    }

    .video-modal__title {
      margin: 0;
      font-size: 15px;
      font-weight: 500;
    }

    .video-modal__close {
      border: none;
      background: none;
      font-size: 16px;
      color: #999;
      cursor: pointer;
      padding: 4px 8px;
    }

    .video-modal__close:hover {
      color: #333;
    }

    .video-modal__body {
      padding: 20px;
      overflow: auto;
    }

    .video-modal__player {
      width: 100%;
      max-height: calc(90vh - 100px);
      border-radius: 4px;
    }

    .video-modal__loader {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 12px;
      padding: 60px 0;
      color: #666;
      font-size: 14px;
    }

    .video-modal__loader i {
      font-size: 28px;
      color: #4fc3f7;
    }
  `]
})
export class VideoPlayerModalComponent {
  @ViewChild('videoEl') videoEl?: ElementRef<HTMLVideoElement>;
  @Output() closed = new EventEmitter<void>();

  isOpen = false;
  loading = false;
  src: string = '';
  title: string = '';

  constructor(private readonly cdr: ChangeDetectorRef) {}

  /** Open the modal with a spinner — call this immediately on click, then setSource once the blob is ready. */
  openLoading(title: string): void {
    this.title = title;
    this.src = '';
    this.loading = true;
    this.isOpen = true;
    this.cdr.detectChanges();
  }

  /** Swap the spinner for the <video> element once the URL is available. */
  setSource(src: string): void {
    this.src = src;
    this.loading = false;
    this.cdr.detectChanges();
  }

  /** Legacy one-shot — opens directly with a ready URL. */
  open(src: string, title: string): void {
    this.openLoading(title);
    this.setSource(src);
  }

  close(): void {
    if (this.videoEl?.nativeElement) {
      const el = this.videoEl.nativeElement;
      el.pause();
      el.removeAttribute('src');
      el.load();
    }
    this.src = '';
    this.loading = false;
    this.isOpen = false;
    this.cdr.detectChanges();
    this.closed.emit();
  }

  onOverlayClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.close();
    }
  }
}
